import { useCallback, useEffect, useRef, useState } from 'react';
import { formatHumanReadableTimeLeft } from '../utils/timeFormat';

const formatTimeLeft = (timeLeft: number) => {
  const minutes = Math.floor((timeLeft / 1000 / 60) % 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor((timeLeft / 1000) % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const baseClassName = 'countdown';
const createCountdownClassName = (timeLeft: number) => {
  if (timeLeft <= 0) {
    return `${baseClassName}--expired`;
  }
  // 10 seconds
  if (timeLeft <= WARNING_THRESHOLD_MS) {
    return `${baseClassName}--warning`;
  }
  return `${baseClassName}--normal`;
};

/** Threshold when the element will have a 'warning' style applied */
const WARNING_THRESHOLD_MS = 10000;
/** Interval tick rate */
export const COUNTDOWN_INTERVAL_TICK_MS = 1000;

export interface CountdownProps {
  /** End time for the basis of the countdown; initially as a difference from Date.now() */
  endTimeInMs: number;
  /** Set to true to pause the countdown */
  paused?: boolean;
  /** Format of the countdown text */
  format?: 'mm:ss' | 'human';
}

/**
 * A pause-able Countdown component that will render a human-readable countdown element with
 * customizable styles
 */
export function Countdown({ endTimeInMs, format = 'mm:ss', paused = false }: CountdownProps) {
  const [timeLeft, setTimeLeft] = useState(endTimeInMs - Date.now());
  const interval = useRef<NodeJS.Timeout | null>(null);

  const setupInterval = useCallback(() => {
    interval.current = setInterval(() => {
      if (endTimeInMs <= Date.now()) {
        setTimeLeft(0);
        cleanupInterval();
        return;
      }

      setTimeLeft((timeLeft) => timeLeft - COUNTDOWN_INTERVAL_TICK_MS);
    }, COUNTDOWN_INTERVAL_TICK_MS);
  }, [endTimeInMs]);

  const cleanupInterval = () => {
    if (interval.current) {
      clearInterval(interval.current);
    }
  };

  useEffect(() => {
    if (paused) {
      cleanupInterval();
    } else {
      setupInterval();
    }
    return () => cleanupInterval();
  }, [paused, setupInterval]);

  return (
    <span
      className={`${baseClassName} ${createCountdownClassName(timeLeft)}`}
      data-testid="countdown-text"
    >
      {format === 'mm:ss' ? formatTimeLeft(timeLeft) : formatHumanReadableTimeLeft(timeLeft)}
    </span>
  );
}
